<template>
  <div class="category-management" :dir="isHebrew ? 'rtl' : 'ltr'">
    <NavBar />
    
    <div class="container-fluid py-4">
      <div class="row" :class="{ 'flex-row-reverse': isHebrew }">
        <!-- Left Panel: Categories List -->
        <div class="col-md-4">
          <div class="card shadow">
            <div class="card-header bg-white border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">{{ t('adminCategoriesTitle') }}</h3>
                </div>
                <div class="col" :class="isHebrew ? 'text-left' : 'text-right'">
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="addCategory"
                    class="mb-2"
                  >
                    {{ t('adminCategoriesAddCategory') }}
                  </b-button>
                  <b-form-input
                    v-model="searchQuery"
                    :placeholder="t('adminCategoriesSearchPlaceholder')"
                    class="mb-2"
                  />
                </div>
              </div>
            </div>
            
            <div class="card-body">
              <div v-if="loading" class="text-center py-4">
                <b-spinner variant="primary" />
              </div>
              
              <div v-else-if="!categories || filteredCategories.length === 0" class="text-center py-4">
                <p class="text-muted">{{ t('adminCategoriesNoCategoriesFound') }}</p>
              </div>
              
              <div v-else class="categories-list">
                <div
                  v-for="category in filteredCategories"
                  :key="category.id"
                  class="category-item p-3 border-bottom"
                  :class="{ 'active': selectedCategory?.id === category.id }"
                  @click="selectCategory(category)"
                >
                  <div class="d-flex align-items-center">
                    <div>
                      <h5 class="mb-1">{{ category.name || t('adminCategoriesUnnamedCategory') }}</h5>
                      <small class="text-muted">
                        {{ category.subcategories?.length || 0 }} {{ t('adminCategoriesSubcategoriesCount') }}
                      </small>
                    </div>
                    <div class="ml-auto">
                      <b-button
                        variant="link"
                        class="p-0"
                        @click.stop="toggleExpand(category)"
                      >
                        <i
                          class="fas"
                          :class="expandedCategories[category.id] ? 'fa-chevron-up' : 'fa-chevron-down'"
                        />
                      </b-button>
                    </div>
                  </div>
                  
                  <!-- Subcategories -->
                  <div v-if="expandedCategories[category.id] && category.subcategories" class="mt-3">
                    <div
                      v-for="subcategory in category.subcategories"
                      :key="subcategory.id"
                      class="subcategory-item pl-4 py-2"
                    >
                      {{ subcategory.name || t('adminCategoriesUnnamedSubcategory') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Right Panel: Category Details -->
        <div class="col-md-8">
          <div v-if="selectedCategory" class="card shadow">
            <div class="card-header bg-white border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">{{ selectedCategory.name || t('adminCategoriesUnnamedCategory') }}</h3>
                </div>
                <div class="col" :class="isHebrew ? 'text-left' : 'text-right'">
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="editCategory"
                  >
                    {{ t('adminCategoriesEditCategory') }}
                  </b-button>
                  <b-button
                    variant="danger"
                    size="sm"
                    class="ml-2"
                    @click="confirmDeleteCategory"
                  >
                    {{ t('adminCategoriesDelete') }}
                  </b-button>
                </div>
              </div>
            </div>
            
            <div class="card-body">
              <h4>{{ t('adminCategoriesSubcategories') }}</h4>
              <div v-if="selectedCategory.subcategories && selectedCategory.subcategories.length > 0" class="subcategories-list">
                <div
                  v-for="subcategory in selectedCategory.subcategories"
                  :key="subcategory.id"
                  class="subcategory-item d-flex align-items-center p-3 border-bottom"
                >
                  <div>
                    <h5 class="mb-1">{{ subcategory.name || t('adminCategoriesUnnamedSubcategory') }}</h5>
                  </div>
                  <div class="ml-auto">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      class="mr-2"
                      @click="editSubcategory(subcategory)"
                    >
                      {{ t('adminCategoriesEdit') }}
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="confirmDeleteSubcategory(subcategory)"
                    >
                      {{ t('adminCategoriesDelete') }}
                    </b-button>
                  </div>
                </div>
              </div>
              <div v-else class="text-center py-4">
                <p class="text-muted">{{ t('adminCategoriesNoSubcategoriesFound') }}</p>
              </div>
              
              <div class="mt-4">
                <b-button
                  variant="success"
                  @click="addSubcategory"
                >
                  {{ t('adminCategoriesAddSubcategory') }}
                </b-button>
              </div>
            </div>
          </div>
          
          <div v-else class="card shadow">
            <div class="card-body text-center py-5">
              <p class="text-muted">{{ t('adminCategoriesSelectCategory') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modals -->
    <b-modal
      v-model="showDeleteModal"
      :title="t('adminCategoriesConfirmDelete')"
      @ok="handleDelete"
    >
      <p>{{ deleteConfirmMessage }}</p>
      <p class="text-danger">{{ t('adminCategoriesCannotUndo') }}</p>
    </b-modal>
    
    <b-modal
      v-model="showEditModal"
      :title="getModalTitle"
      @ok="handleSave"
      @hide="handleModalHide"
    >
      <b-form-group
        :label="t('adminCategoriesName')"
        label-for="item-name"
      >
        <b-form-input
          id="item-name"
          v-model="editingItem.name"
          required
          :placeholder="t('adminCategoriesEnterName')"
          @keyup.enter="handleSave"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBar from '@/components/NavBar.vue';
import { translationMixin } from '@/mixins/translationMixin';

import { INTERNAL_HOME_PATH } from '@/common/appHome';
export default {
  name: 'CategoryManagement',
  mixins: [translationMixin],
  
  components: {
    NavBar
  },
  
  data() {
    return {
      loading: false,
      searchQuery: '',
      selectedCategory: null,
      expandedCategories: {},
      showDeleteModal: false,
      showEditModal: false,
      itemToDelete: null,
      editingItem: { name: '' },  // Initialize with empty object
      editingSubcategory: false
    };
  },
  
  computed: {
    ...mapGetters([
      'isUserManager',
      'categories'
    ]),
    
    getModalTitle() {
      if (!this.editingItem) return '';
      if (this.editingSubcategory) {
        return this.editingItem.id ? this.t('adminCategoriesEditSubcategoryTitle') : this.t('adminCategoriesAddSubcategoryTitle');
      }
      return this.editingItem.id ? this.t('adminCategoriesEditCategoryTitle') : this.t('adminCategoriesAddCategoryTitle');
    },
    
    deleteConfirmMessage() {
      const name = this.itemToDelete?.name || this.t('adminCategoriesThisItem');
      return this.t('adminCategoriesConfirmDeleteMessage').replace('{0}', name);
    },
    
    filteredCategories() {
      if (!this.categories) {
        return [];
      }
      
      if (!this.searchQuery) {
        return this.categories;
      }
      
      const query = this.searchQuery.toLowerCase();
      return this.categories.filter(category => {
        if (!category || !category.name) {
          return false;
        }
        const matchesCategory = category.name.toLowerCase().includes(query);
        const matchesSubcategory = category.subcategories?.some(
          sub => sub && sub.name && sub.name.toLowerCase().includes(query)
        );
        return matchesCategory || matchesSubcategory;
      });
    }
  },
  
  created() {
    if (!this.isUserManager) {
      this.$router.push(INTERNAL_HOME_PATH);
      return;
    }
    
    this.fetchCategories();
  },
  
  methods: {
    async fetchCategories() {
      this.loading = true;
      try {
        await this.$store.dispatch('fetchCategories');
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        this.$bvToast.toast(this.t('adminCategoriesFailedToLoad'), {
          title: this.t('adminCategoriesError'),
          variant: 'danger'
        });
      }
      this.loading = false;
    },
    
    selectCategory(category) {
      this.selectedCategory = category;
    },
    
    toggleExpand(category) {
      if (!category || !category.id) return;
      
      this.$set(
        this.expandedCategories,
        category.id,
        !this.expandedCategories[category.id]
      );
    },
    
    addCategory() {
      this.editingItem = { name: '' };
      this.editingSubcategory = false;
      this.showEditModal = true;
    },
    
    editCategory() {
      if (!this.selectedCategory) return;
      
      this.editingItem = { 
        id: this.selectedCategory.id,
        name: this.selectedCategory.name || ''
      };
      this.editingSubcategory = false;
      this.showEditModal = true;
    },
    
    addSubcategory() {
      if (!this.selectedCategory) return;
      
      this.editingItem = { name: '' };
      this.editingSubcategory = true;
      this.showEditModal = true;
    },
    
    editSubcategory(subcategory) {
      if (!subcategory) return;
      
      this.editingItem = { 
        id: subcategory.id,
        name: subcategory.name || ''
      };
      this.editingSubcategory = true;
      this.showEditModal = true;
    },
    
    confirmDeleteCategory() {
      if (!this.selectedCategory) return;
      
      this.itemToDelete = this.selectedCategory;
      this.showDeleteModal = true;
    },
    
    confirmDeleteSubcategory(subcategory) {
      if (!subcategory) return;
      
      this.itemToDelete = subcategory;
      this.showDeleteModal = true;
    },
    
    async handleDelete() {
      if (!this.itemToDelete) return;
      
      try {
        if (this.itemToDelete === this.selectedCategory) {
          await this.$store.dispatch('deleteCategory', this.itemToDelete.id);
          this.selectedCategory = null;
        } else {
          await this.$store.dispatch('deleteSubcategory', {
            categoryId: this.selectedCategory.id,
            subcategoryId: this.itemToDelete.id
          });
        }
        
        this.$bvToast.toast(this.t('adminCategoriesSuccessfullyDeleted'), {
          title: this.t('adminCategoriesSuccess'),
          variant: 'success'
        });
      } catch (error) {
        console.error('Failed to delete:', error);
        this.$bvToast.toast(this.t('adminCategoriesFailedToDelete'), {
          title: this.t('adminCategoriesError'),
          variant: 'danger'
        });
      }
      
      this.itemToDelete = null;
    },
    
    async handleSave() {
      if (!this.editingItem || !this.editingItem.name) {
        this.$bvToast.toast(this.t('adminCategoriesNameRequired'), {
          title: this.t('adminCategoriesValidationError'),
          variant: 'danger'
        });
        return;
      }
      
      try {
        if (this.editingSubcategory) {
          if (!this.selectedCategory) return;
          
          if (this.editingItem.id) {
            await this.$store.dispatch('updateSubcategory', {
              categoryId: this.selectedCategory.id,
              subcategory: this.editingItem
            });
          } else {
            await this.$store.dispatch('createSubcategory', {
              categoryId: this.selectedCategory.id,
              subcategory: this.editingItem
            });
          }
        } else {
          if (this.editingItem.id) {
            await this.$store.dispatch('updateCategory', this.editingItem);
          } else {
            await this.$store.dispatch('createCategory', this.editingItem);
          }
        }
        
        this.$bvToast.toast(this.t('adminCategoriesSuccessfullySaved'), {
          title: this.t('adminCategoriesSuccess'),
          variant: 'success'
        });
        this.showEditModal = false;
      } catch (error) {
        console.error('Failed to save:', error);
        this.$bvToast.toast(this.t('adminCategoriesFailedToSave'), {
          title: this.t('adminCategoriesError'),
          variant: 'danger'
        });
      }
      
      this.editingItem = { name: '' };  // Reset to empty object
    },

    handleModalHide() {
      // Reset form when modal is closed
      this.editingItem = { name: '' };
      this.editingSubcategory = false;
    }
  }
};
</script>

<style scoped>
.category-management {
  min-height: 100vh;
  background-color: #f8f9fe;
  padding-top: 72px; /* Add padding to account for fixed navbar */
}

.categories-list {
  max-height: calc(100vh - 200px); /* Adjust max height to account for navbar */
  overflow-y: auto;
}

.category-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.category-item:hover {
  background-color: #f8f9fe;
}

.category-item.active {
  background-color: #e9ecef;
}

.subcategory-item {
  background-color: #fff;
  transition: background-color 0.2s;
}

.subcategory-item:hover {
  background-color: #f8f9fe;
}

/* Add smooth scrolling */
.categories-list {
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

/* Add scrollbar styling */
.categories-list::-webkit-scrollbar {
  width: 8px;
}

.categories-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.categories-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.categories-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Ensure cards have proper spacing */
.card {
  margin-bottom: 1rem;
}

/* Make buttons more consistent */
.btn {
  min-width: 80px;
}

/* Improve form input spacing */
.form-group {
  margin-bottom: 1rem;
}

.form-control {
  margin-bottom: 0.5rem;
}
</style> 